import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCampaignLeads, updateCampaignLeadStatus } from '../services/api';

const CAMPAIGN_STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'converted', 'closed', 'spam'];

const Campaign = () => {
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [updatingId, setUpdatingId] = useState(null);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('adminToken');

  const fetchLeads = useCallback(async (page = 1, appliedFilters = null) => {
    const f = appliedFilters !== null ? appliedFilters : filters;
    const token = getToken();
    setLoading(true);
    setError(null);
    setAuthError(false);
    try {
      const params = { page, limit: 20 };
      if (f.status) params.status = f.status;
      if (f.search && f.search.trim()) params.search = f.search.trim();
      const res = await getCampaignLeads(params, token);
      if (res.success && res.data) {
        setLeads(res.data.leads || []);
        setPagination(res.data.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthError(true);
        setLeads([]);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load campaign leads.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLeads(pagination.page);
  }, [pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on page change only; filters applied on Apply

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchLeads(1, filters);
  };

  const handleStatusChange = async (leadId, newStatus) => {
    const token = getToken();
    if (!token) return;
    setUpdatingId(leadId);
    try {
      const res = await updateCampaignLeadStatus(leadId, { status: newStatus }, token);
      if (res.success && res.data) {
        setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, status: res.data.status } : l)));
      }
    } catch (err) {
      // keep UI as is on error
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (typeof window.WOW !== 'undefined') new window.WOW().init();
  }, []);

  return (
    <>
      <div className="page-title" style={{ backgroundImage: 'url(/images/resource/pageheaderimage.jpg)' }}>
        <div className="auto-container">
          <h1>Campaign Leads</h1>
        </div>
      </div>
      <div className="bredcrumb-wrap">
        <div className="auto-container">
          <ul className="bredcrumb-list">
            <li><Link to="/">Home</Link></li>
            <li>Campaign</li>
          </ul>
        </div>
      </div>

      <section className="campaign-section">
        <div className="auto-container">
          <div className="section_heading mb_30">
            <span className="section_heading_title_small">Meta Lead Ads & Conversions API</span>
            <h2 className="section_heading_title_big">Leads from <span>Campaigns</span></h2>
            <p>Leads from Facebook/Instagram Lead Ads (via webhook) and data connected to Meta&apos;s Conversions API.</p>
          </div>

          {authError && (
            <div className="alert alert-warning mb_4">
              <strong>Login required.</strong> Please log in to the admin panel to view campaign leads. Store your JWT in <code>localStorage.token</code> or <code>localStorage.adminToken</code>.
            </div>
          )}

          {!authError && (
            <>
              <form onSubmit={handleFilterSubmit} className="campaign-filters mb_30 d-flex flex-wrap gap-2 align-items-end">
                <div>
                  <label className="d-block mb-1 small">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="form-control"
                    style={{ minWidth: '140px' }}
                  >
                    <option value="">All</option>
                    {CAMPAIGN_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="d-block mb-1 small">Search</label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Name, email, phone..."
                    className="form-control"
                    style={{ minWidth: '180px' }}
                  />
                </div>
                <button type="submit" className="btn-1 btn-sm">Apply</button>
              </form>

              {error && <div className="alert alert-danger mb_4">{error}</div>}

              {loading ? (
                <p className="text-muted">Loading campaign leads…</p>
              ) : leads.length === 0 ? (
                <p className="text-muted">No campaign leads found. Configure the Meta Leadgen webhook and run Lead Ads to receive leads here.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered campaign-leads-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Form / Ad</th>
                        <th>Source</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l._id}>
                          <td>{formatDate(l.metaCreatedTime || l.createdAt)}</td>
                          <td>{l.name || '—'}</td>
                          <td>{l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : '—'}</td>
                          <td>{l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '—'}</td>
                          <td>{l.formName || l.formId || '—'}</td>
                          <td><span className="badge bg-secondary">{l.source || 'meta_lead_ad'}</span></td>
                          <td>
                            <select
                              value={l.status || 'new'}
                              onChange={(e) => handleStatusChange(l._id, e.target.value)}
                              disabled={updatingId === l._id}
                              className="form-select form-select-sm"
                              style={{ maxWidth: '130px' }}
                            >
                              {CAMPAIGN_STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && pagination.pages > 1 && (
                <nav className="mt-4 d-flex justify-content-between align-items-center">
                  <span className="text-muted">
                    Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                  </span>
                  <div className="d-flex gap-1">
                    <button
                      type="button"
                      className="btn-1 btn-sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn-1 btn-sm"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                    >
                      Next
                    </button>
                  </div>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Campaign;
